import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { inr, inrWords, ddmmyyyy } from "../lib/format";
import { pc } from "../pricing";
import { SectionTitle, Pill, EmptyState } from "../components/common";
import { UpgradeBanner, PlansSheet, TaxPackBanner, RevenueSnapshot, BoostInsights, RenewalNudge, BoostHotNudge } from "../components/monetization";
import { usePricing, purchase } from "../pricing";
import { Plus, FileText, Bell, Wrench, Building2, Receipt, RefreshCw, ArrowRight, Home as HomeIcon, KeyRound, IndianRupee, TrendingUp } from "lucide-react";

const RENT_REMINDER_HINT = "reminders go out 5 days before rent is due";

function QuickAction({ icon: Icon, label, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className="gs-card p-3 flex flex-col items-center gap-1.5 active:scale-95 transition">
      <div className="w-10 h-10 rounded-xl bg-terracotta-light flex items-center justify-center text-terracotta"><Icon className="w-5 h-5" /></div>
      <span className="text-[11px] font-semibold text-slate-600 text-center leading-tight">{label}</span>
    </button>
  );
}

export default function Home() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [ownerValue, setOwnerValue] = useState(null);
  const [boostInsights, setBoostInsights] = useState([]);
  const [endingSoon, setEndingSoon] = useState([]);
  const { config, status, reload } = usePricing();
  const [plansOpen, setPlansOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [p, m, a, r] = await Promise.all([api.get("/payments"), api.get("/maintenance"), api.get("/agreements"), api.get("/reminders")]);
      setPayments(p.data); setTickets(m.data); setAgreements(a.data); setReminders(r.data);
      if (user?.role === "owner") {
        const [l, v, b, e] = await Promise.all([
          api.get("/listings/mine"),
          api.get("/monetization/owner-value"),
          api.get("/monetization/boost-insights"),
          api.get("/agreements/ending-soon"),
        ]);
        setListings(l.data); setOwnerValue(v.data); setBoostInsights(b.data.listings); setEndingSoon(e.data);
      }
    } catch (e) { console.warn("Home data load failed", e); }
  }, [user?.role]);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (user?.role === "admin") navigate("/admin", { replace: true }); }, [user, navigate]);

  const previewReminder = async () => {
    try { const { data } = await api.post("/reminders/run-now"); setReminders((prev) => { const ids = new Set(prev.map((x) => x.key)); return [...data.created.filter((x) => !ids.has(x.key)), ...prev]; }); }
    catch (e) { console.warn("Preview reminder failed", e); }
  };

  const isOwner = user?.role === "owner";
  const collected = payments.filter((p) => p.kind === "rent").reduce((s, p) => s + p.amount, 0);
  const openTickets = tickets.filter((x) => x.status !== "resolved");

  return (
    <div className="space-y-5">
      <div className="gs-rise">
        <p className="text-sm text-slate-500">{t("welcome")},</p>
        <h1 className="font-head text-2xl font-bold text-slate-900">{user?.name} 👋</h1>
      </div>
      <PlansSheet open={plansOpen} onOpenChange={setPlansOpen} config={config} status={status} onChanged={reload} />

      {isOwner ? (
        <>
          <UpgradeBanner onOpen={() => setPlansOpen(true)} />
          <TaxPackBanner active={status?.tax_pack_active} price={config?.prices?.tax_pack?.amount} onGet={() => purchase("tax_pack", { amount: config?.prices?.tax_pack?.amount })} />
          <RenewalNudge items={endingSoon} onRenew={(id) => navigate(`/agreements/new?renew=${id}`)} onRelist={() => navigate("/properties/new")} />
          <BoostHotNudge items={boostInsights} onCreate={(id) => navigate(`/agreements/new?listing=${id}`)} />
          <div className="gs-card p-5 bg-gradient-to-br from-slate2 to-[#0F172A] text-white gs-rise" data-testid="owner-hero-card">
            <div className="flex items-center gap-2 text-slate-300 text-xs font-semibold uppercase tracking-wide"><IndianRupee className="w-3.5 h-3.5" /> {t("rent_collected")}</div>
            <div className="font-mono font-bold text-3xl mt-1">{inr(collected)}</div>
            <div className="flex gap-3 mt-4 text-sm">
              <div className="flex-1 bg-white/10 rounded-xl p-3"><div className="text-slate-300 text-xs">{t("my_properties")}</div><div className="font-bold text-xl">{listings.length}</div></div>
              <div className="flex-1 bg-white/10 rounded-xl p-3"><div className="text-slate-300 text-xs">{t("open_tickets")}</div><div className="font-bold text-xl">{openTickets.length}</div></div>
            </div>
          </div>

          <RevenueSnapshot data={ownerValue} onUpgrade={() => setPlansOpen(true)} />
          <BoostInsights items={boostInsights} />

          <div className="grid grid-cols-4 gap-2.5">
            <QuickAction icon={Plus} label={t("add_property")} onClick={() => navigate("/properties/new")} testId="add-property-wizard-btn" />
            <QuickAction icon={FileText} label={t("gen_agreement")} onClick={() => navigate("/agreements/new")} testId="qa-agreement-btn" />
            <QuickAction icon={Wrench} label={t("track_complaints")} onClick={() => navigate("/maintenance")} testId="qa-complaints-btn" />
            <QuickAction icon={Receipt} label={t("nav_payments")} onClick={() => navigate("/payments")} testId="qa-payments-btn" />
          </div>

          <div>
            <SectionTitle>{t("my_properties")}</SectionTitle>
            {listings.length === 0 ? (
              <EmptyState icon={Building2} title={t("my_properties")} sub={t("add_property")} />
            ) : (
              <div className="space-y-2.5">
                {listings.map((l) => {
                  const bi = boostInsights.find((x) => x.id === l.id);
                  const xViews = bi ? bi.extra_views : Math.max(0, (l.views || 0) - (l.views_at_boost ?? l.views ?? 0));
                  const xEnq = bi ? bi.extra_enquiries : 0;
                  return (
                  <button key={l.id} onClick={() => navigate(`/properties/${l.id}`)} data-testid={`owner-listing-${l.id}`} className="gs-card p-3 w-full flex items-center gap-3 text-left active:scale-[0.99]">
                    <img src={l.photos?.[0] || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=200"} alt="" className="w-16 h-16 rounded-xl object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="font-head font-semibold text-slate-800 truncate">{l.title}</div>
                      <div className="text-xs text-slate-400 truncate">{l.locality}, {l.city}</div>
                      <div className="font-mono font-bold text-terracotta text-sm mt-0.5">{inr(l.rent)}<span className="text-slate-400 text-xs">{t("per_month")}</span></div>
                      {l.featured && (
                        <span data-testid={`boost-badge-${l.id}`} className="inline-flex items-center gap-1.5 mt-1 text-[11px] font-semibold text-amberw bg-amberw-light rounded-full px-2 py-0.5">
                          <TrendingUp className="w-3 h-3" /> {pc(lang, "boost_extra_views", { n: xViews })} · {pc(lang, "boost_extra_enquiries", { n: xEnq })}
                        </span>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-300" />
                  </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="gs-card p-5 gs-rise" data-testid="tenant-hero-card">
            <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold uppercase tracking-wide"><IndianRupee className="w-3.5 h-3.5" /> {t("rent_due")}</div>
            <div className="font-mono font-bold text-3xl mt-1 text-slate-900">{agreements[0] ? inr(agreements[0].rent) : "₹0"}</div>
            <button onClick={() => navigate("/payments")} data-testid="pay-rent-upi-btn" className="gs-tap w-full mt-4 rounded-xl bg-trust hover:bg-trust/90 text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition">
              <IndianRupee className="w-4 h-4" /> {t("pay_upi")}
            </button>
          </div>

          <div className="grid grid-cols-4 gap-2.5">
            <QuickAction icon={KeyRound} label={t("find_home")} onClick={() => navigate("/properties")} testId="qa-find-home-btn" />
            <QuickAction icon={FileText} label={t("my_agreement")} onClick={() => navigate("/agreements")} testId="qa-agreement-btn" />
            <QuickAction icon={Wrench} label={t("raise_complaint")} onClick={() => navigate("/maintenance")} testId="qa-complaint-btn" />
            <QuickAction icon={RefreshCw} label={t("request_renewal")} onClick={() => navigate("/agreements")} testId="qa-renewal-btn" />
          </div>

          {agreements[0] && (
            <div className="gs-card p-4" data-testid="tenant-agreement-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><FileText className="w-5 h-5 text-terracotta" /><span className="font-head font-semibold text-slate-800">{t("my_agreement")}</span></div>
                <Pill tone={agreements[0].status === "active" ? "trust" : "amber"}>{agreements[0].status}</Pill>
              </div>
              <button onClick={() => navigate(`/agreements/${agreements[0].id}`)} data-testid="read-clauses-btn" className="text-sm font-semibold text-terracotta mt-3 flex items-center gap-1">{t("read_clauses")} <ArrowRight className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </>
      )}

      {openTickets.length > 0 && (
        <div>
          <SectionTitle>{t("open_tickets")}</SectionTitle>
          <div className="space-y-2">
            {openTickets.slice(0, 3).map((tk) => (
              <button key={tk.id} onClick={() => navigate("/maintenance")} className="gs-card p-3 w-full flex items-center gap-3 text-left">
                <div className="w-9 h-9 rounded-lg bg-amberw-light flex items-center justify-center text-amberw"><Wrench className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-slate-700 truncate">{tk.description}</div><div className="text-xs text-slate-400">{tk.ai?.category} · {ddmmyyyy(tk.created_at)}</div></div>
                <Pill tone={tk.ai?.urgency === "Emergency" || tk.ai?.urgency === "High" ? "red" : "slate"}>{tk.ai?.urgency}</Pill>
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <SectionTitle>{t("rent_reminders")}</SectionTitle>
          <button onClick={previewReminder} data-testid="preview-reminder-btn" className="flex items-center gap-1 text-sm font-semibold text-terracotta bg-terracotta-light px-3 py-1.5 rounded-full"><Bell className="w-4 h-4" /> {t("preview_reminder")}</button>
        </div>
        {reminders.length === 0 ? (
          <div className="gs-card p-4 text-sm text-slate-400 flex items-center gap-2" data-testid="reminders-empty"><Bell className="w-4 h-4" /> {t("upcoming_renewals")} — {RENT_REMINDER_HINT}</div>
        ) : (
          <div className="space-y-2" data-testid="reminders-list">
            {reminders.slice(0, 4).map((r) => (
              <div key={r.key} className="gs-card p-3 flex items-center gap-3" data-testid={`reminder-${r.key}`}>
                <div className="w-9 h-9 rounded-lg bg-trust-light flex items-center justify-center text-trust"><Bell className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-700">{inr(r.rent)} · {ddmmyyyy(r.due_date)}</div>
                  <div className="text-xs text-slate-400 truncate">{r.message}</div>
                </div>
                <Pill tone={r.delivery?.status === "sent" ? "trust" : "amber"}>{r.delivery?.status === "sent" ? "Sent" : "WhatsApp+SMS"}</Pill>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
